import { useCallback, useState } from 'react';
import { Tldraw, createTLStore, defaultShapeUtils, useEditor } from '@tldraw/tldraw';
import '@tldraw/tldraw/tldraw.css';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useActivityLogger } from '@/hooks/use-activity-logger';

const store = createTLStore({
  shapeUtils: defaultShapeUtils,
});

interface MindMapCanvasProps {
  projectId?: string;
}

export default function MindMapCanvas({ projectId = 'default' }: MindMapCanvasProps) {
  const { currentOrganization } = useAuth();
  const { toast } = useToast();
  const { logActivity } = useActivityLogger();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleMount = useCallback((editor: any) => {
    // Load existing mind map if available
    const loadMindMap = async () => {
      if (!currentOrganization) return;
      
      setIsLoading(true);
      try {
        // Use raw query approach since mindmaps table might not be in the TypeScript types yet
        const { data, error } = await supabase
          .from('mindmaps')
          .select('*')
          .eq('organization_id', currentOrganization.organization.id)
          .eq('project_id', projectId)
          .single();
        
        if (error) {
          if (error.code !== 'PGRST116') { // PGRST116 is "No rows returned" which is expected for new users
            console.error('Error loading mind map:', error);
          }
          return;
        }
        
        if (data && data.content) {
          // Fix: Safely handle JSON parsing by ensuring we have a string
          const contentString = typeof data.content === 'string' 
            ? data.content 
            : JSON.stringify(data.content);
            
          editor.store.loadSnapshot(JSON.parse(contentString));
          toast({
            title: "Mind map loaded",
            description: "Your mind map has been loaded successfully",
          });
          
          logActivity({
            action: 'view',
            resourceType: 'document',
            resourceId: data.id,
            details: { type: 'mindmap', projectId }
          });
        }
      } catch (error) {
        console.error('Error loading mind map:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadMindMap();
  }, [currentOrganization, projectId, toast, logActivity]);

  const handleSave = useCallback(async (editor: any) => {
    if (!currentOrganization) return;
    
    setIsSaving(true);
    try {
      // Save the current state of the mind map
      const snapshot = editor.store.getSnapshot();
      const snapshotJson = JSON.stringify(snapshot);
      
      // Check if mind map already exists using raw query
      const { data: existingData, error: fetchError } = await supabase
        .from('mindmaps')
        .select('id')
        .eq('organization_id', currentOrganization.organization.id)
        .eq('project_id', projectId)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking for existing mind map:', fetchError);
        toast({
          title: "Save failed",
          description: "There was an error saving your mind map",
          variant: "destructive",
        });
        return;
      }
      
      if (existingData) {
        // Update existing mind map
        const { error: updateError } = await supabase
          .from('mindmaps')
          .update({
            content: snapshotJson,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingData.id);
        
        if (updateError) {
          console.error('Error updating mind map:', updateError);
          toast({
            title: "Save failed",
            description: "There was an error updating your mind map",
            variant: "destructive",
          });
          return;
        }

        logActivity({
          action: 'update',
          resourceType: 'document',
          resourceId: existingData.id,
          details: { type: 'mindmap', projectId }
        });
      } else {
        // Create new mind map
        const { data: newMindMap, error: insertError } = await supabase
          .from('mindmaps')
          .insert({
            organization_id: currentOrganization.organization.id,
            project_id: projectId,
            content: snapshotJson,
            name: `Mind Map - ${new Date().toLocaleDateString()}`,
          })
          .select('id')
          .single();
        
        if (insertError) {
          console.error('Error creating mind map:', insertError);
          toast({
            title: "Save failed",
            description: "There was an error creating your mind map",
            variant: "destructive",
          });
          return;
        }

        logActivity({
          action: 'create',
          resourceType: 'document',
          resourceId: newMindMap.id,
          details: { type: 'mindmap', projectId }
        });
      }
      
      toast({
        title: "Mind map saved",
        description: "Your mind map has been saved successfully",
      });
    } catch (error) {
      console.error('Error saving mind map:', error);
      toast({
        title: "Save failed",
        description: "There was an error saving your mind map",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentOrganization, projectId, toast, logActivity]);

  // Create a component for the toolbar that includes the export functionality
  const TldrawToolbar = useCallback(() => {
    const editor = useEditor();
    
    const handleExportPNG = async () => {
      if (!editor) return;
      
      try {
        // Get the SVG element from the editor
        const selectedShapeIds = editor.getSelectedShapeIds();
        const svg = await editor.getSvg(
          selectedShapeIds.length > 0 
            ? selectedShapeIds 
            : Array.from(editor.getCurrentPageShapeIds().values())
        );
          
        if (!svg) return;
        
        // Convert SVG to PNG
        const pngBlob = await new Promise<Blob>((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
            }, 'image/png');
          };
          
          img.src = 'data:image/svg+xml;base64,' + btoa(new XMLSerializer().serializeToString(svg));
        });
        
        // Create download link
        const url = URL.createObjectURL(pngBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mindmap-${projectId}-${new Date().toISOString().slice(0, 10)}.png`;
        link.click();
        URL.revokeObjectURL(url);
        
        logActivity({
          action: 'export',
          resourceType: 'document',
          details: { type: 'mindmap', format: 'png', projectId }
        });
        
        toast({
          title: "Export successful",
          description: "Your mind map has been exported as PNG",
        });
      } catch (error) {
        console.error('Error exporting as PNG:', error);
        toast({
          title: "Export failed",
          description: "There was an error exporting your mind map",
          variant: "destructive",
        });
      }
    };

    const handleExportSVG = async () => {
      if (!editor) return;
      
      try {
        // Get the SVG element from the editor
        const selectedShapeIds = editor.getSelectedShapeIds();
        const svg = await editor.getSvg(
          selectedShapeIds.length > 0 
            ? selectedShapeIds 
            : Array.from(editor.getCurrentPageShapeIds().values())
        );
          
        if (!svg) return;
        
        // Create download link
        const svgString = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svgBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mindmap-${projectId}-${new Date().toISOString().slice(0, 10)}.svg`;
        link.click();
        URL.revokeObjectURL(url);
        
        logActivity({
          action: 'export',
          resourceType: 'document',
          details: { type: 'mindmap', format: 'svg', projectId }
        });
        
        toast({
          title: "Export successful",
          description: "Your mind map has been exported as SVG",
        });
      } catch (error) {
        console.error('Error exporting as SVG:', error);
        toast({
          title: "Export failed",
          description: "There was an error exporting your mind map",
          variant: "destructive",
        });
      }
    };

    const handleExportJSON = () => {
      if (!editor) return;
      
      try {
        // Get the current state of the editor
        const snapshot = editor.store.getSnapshot();
        const jsonString = JSON.stringify(snapshot, null, 2);
        
        // Create download link
        const jsonBlob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(jsonBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mindmap-${projectId}-${new Date().toISOString().slice(0, 10)}.json`;
        link.click();
        URL.revokeObjectURL(url);
        
        logActivity({
          action: 'export',
          resourceType: 'document',
          details: { type: 'mindmap', format: 'json', projectId }
        });
        
        toast({
          title: "Export successful",
          description: "Your mind map has been exported as JSON",
        });
      } catch (error) {
        console.error('Error exporting as JSON:', error);
        toast({
          title: "Export failed", 
          description: "There was an error exporting your mind map",
          variant: "destructive",
        });
      }
    };
    
    return (
      <div className="flex gap-2 justify-between w-full p-2 bg-background/80 border-b">
        <div className="flex items-center gap-2">
          <button
            className="tlui-button tlui-button__normal"
            onClick={() => handleSave(editor)}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Mind Map'}
          </button>
          
          <div className="flex items-center ml-2">
            <div className="relative inline-block">
              <button className="tlui-button tlui-button__normal">
                Export
              </button>
              <div className="absolute left-0 mt-1 w-48 bg-background border rounded-md shadow-lg z-10 hidden group-hover:block">
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-accent"
                  onClick={handleExportPNG}
                >
                  Export as PNG
                </button>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-accent"
                  onClick={handleExportSVG}
                >
                  Export as SVG
                </button>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-accent"
                  onClick={handleExportJSON}
                >
                  Export as JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [handleSave, isSaving, projectId, logActivity, toast]);

  return (
    <div className="border rounded-md h-[600px] relative">
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
          <div className="animate-pulse text-lg">Loading mind map...</div>
        </div>
      )}
      <Tldraw
        store={store}
        onMount={handleMount}
        components={{
          InFrontOfTheCanvas: TldrawToolbar
        }}
      />
    </div>
  );
}
